import {Component, OnInit} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {MenuItemModel} from 'src/app/pos-panel/models/menu-items/menu-item.model';
import {AddOrEditMenuModel} from 'src/app/pos-panel/models/menus/add-or-edit-menu.model';
import {MenuService} from "../../../services/menus/menu.service";
import {DynamicDialogConfig, DynamicDialogRef} from "primeng/dynamicdialog";
import {MessageService} from "primeng/api";
import {CategoryModel} from "../../../models/categories/category.model";
import {CurrencyModel} from "../../../models/menus/currency.model";
import {SubcategoryModel} from "../../../models/subcategories/subcategory.model";
import {CategoryToAddModel} from "../../../models/menus/category-to-add.model";
import {SubcategoryToAddModel} from "../../../models/menus/subcategory-to-add.model";
import {AddOrEditMenuRequestModel} from "../../../models/menus/add-or-edit-request.model";

@Component({
  selector: 'app-add-or-edit-menu',
  templateUrl: './add-or-edit-menu.component.html',
  styleUrls: ['./add-or-edit-menu.component.scss']
})
export class AddOrEditMenuComponent implements OnInit {

  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public menuModel: AddOrEditMenuModel = new AddOrEditMenuModel();
  public errorModel: { [k: string]: any } = {};
  public spinner: boolean = false;
  public menuId: string;
  public companyId: string;
  public currencies: Array<CurrencyModel> = new Array<CurrencyModel>();
  public enableEdit: boolean = false;
  public needUpdateStructure: boolean = false;
  public imagePath: string = './assets/images/logo/category.png';
  public selectedCurrency: string | undefined;
  public currencyFormDisabled: boolean = true;

  public listMenuItems: Array<MenuItemModel> = new Array<MenuItemModel>();
  public listMenuItemsSelected: Array<MenuItemModel> = new Array<MenuItemModel>();

  public categoriesDropdownHelperModels: Array<CategoryModel> = new Array<CategoryModel>();
  public subcategoriesDropdownHelperModels: Array<SubcategoryModel> = new Array<SubcategoryModel>();

  public requiredFieldErrorMessage = "INFO.REQUIRED_FIELD";

  //#endregion
  //#region Constructor
  constructor(private config: DynamicDialogConfig,
              private ref: DynamicDialogRef,
              private messageService: MessageService,
              private menuService: MenuService,
  ) {
    if (this.config.data != undefined && this.config.data.id) {
      this.menuId = this.config.data.id;
      this.enableEdit = true;
    }

    if (this.config.data != undefined && this.config.data.companyId) {
      this.companyId = this.config.data.companyId;
    }
  }

  ngOnInit(): void {
    this.loadCurrencies();
    this.loadCategoriesForCompany();
  }

  //#endregion
  //#region Public Methods
  loadCurrencies(): void {
    this.currencies = new Array<CurrencyModel>();
    this.spinner = true;
    this.menuService.listCurrencies(this.companyId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.currencies = response.filter((x: CurrencyModel) => x.name !== '');
          this.selectedCurrency = this.currencies.find(x => x.isDefault)?.id;
          if (this.selectedCurrency === undefined) {
            this.currencyFormDisabled = false;
          }
          this.spinner = false;
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  loadCategoriesForCompany() {
    this.categoriesDropdownHelperModels = new Array<CategoryModel>();
    this.spinner = true;
    this.menuService.listCategoryForCompany(this.companyId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.spinner = false;
          this.categoriesDropdownHelperModels = response;
          if (this.enableEdit) {
            this.loadMenuDetails();
          }
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  loadSubcategories(selectedCategoryId: string, selectAll: boolean = true) {
    this.spinner = true;

    this.menuService.listSubcategoryForCategory(selectedCategoryId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.spinner = false;
          let withCategory = new Array<SubcategoryModel>();
          response.forEach((subcategory: SubcategoryModel) => {
            let subcat = JSON.parse(JSON.stringify(subcategory));
            subcat.categoryId = selectedCategoryId;
            withCategory.push(subcat);
            if (selectAll) {
              this.menuModel.subcategoryIds.push(subcat.id);
            }
            this.onChangeSubcategory(subcat.id, !this.enableEdit, selectAll);
          });
          this.subcategoriesDropdownHelperModels = [...this.subcategoriesDropdownHelperModels, ...withCategory];
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  addMenu(model: AddOrEditMenuModel) {
    let requestBody = this.buildRequestBody(model);

    this.errorModel = {};
    if (!requestBody.name || requestBody.name == "") {
      this.errorModel['Name'] = this.requiredFieldErrorMessage;
    }
    if (!requestBody.categories || requestBody.categories.length === 0) {
      this.errorModel['CategoryId'] = this.requiredFieldErrorMessage;
    }
    if (!requestBody.defaultCurrencyId || requestBody.defaultCurrencyId === "") {
      this.errorModel['Currency'] = this.requiredFieldErrorMessage;
    }

    if (!this.errorModel['Name'] && !this.errorModel['CategoryId'] && !this.errorModel['Currency']) {
      this.spinner = true;

      this.menuService.createMenu(requestBody)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (response) => {
            this.spinner = false;
            this.ref.close(true)
          },
          error: errorResponse => {
            this.spinner = false;
            this.messageService.add({severity: 'error', summary: "System error", life: 2000});
          }
        });
    }
  }

  editMenu(model: AddOrEditMenuModel) {
    let requestBody = this.buildRequestBody(model);
    requestBody.needUpdateStructure = this.needUpdateStructure;

    this.errorModel = {};
    if (!requestBody.name || requestBody.name == "") {
      this.errorModel['Name'] = this.requiredFieldErrorMessage;
    }
    if (!requestBody.categories || requestBody.categories.length === 0) {
      this.errorModel['CategoryId'] = this.requiredFieldErrorMessage;
    }
    if (!requestBody.defaultCurrencyId || requestBody.defaultCurrencyId === "") {
      this.errorModel['Currency'] = this.requiredFieldErrorMessage;
    }

    if (!this.errorModel['Name'] && !this.errorModel['CategoryId'] && !this.errorModel['Currency']) {
      this.spinner = true;
      
      this.menuService.editMenu(requestBody)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (response) => {
            this.spinner = false;
            this.ref.close(true)
          },
          error: errorResponse => {
            this.spinner = false;
            this.messageService.add({severity: 'error', summary: "System error", life: 2000});
          }
        });
    }
  }

  onChangeCategory(category: any, selectAll: boolean = true) {
    category = category.itemValue ? category.itemValue : category;
    if (this.menuModel.categoryIds.includes(category)) {
      // on select category
      this.loadSubcategories(category, selectAll);
      this.loadMenuItemsForCategory(category, selectAll);
    } else {
      // on deselect category
      this.subcategoriesDropdownHelperModels.forEach(subcategory => {
        if (subcategory.categoryId === category) {
          this.listMenuItems = this.listMenuItems.filter(x => x.companyMenuCategoryId !== subcategory.id);
          this.listMenuItemsSelected = this.listMenuItemsSelected.filter(x => x.companyMenuCategoryId !== subcategory.id);
          this.menuModel.subcategoryIds = this.menuModel.subcategoryIds.filter(y => y !== subcategory.id);
        }
      });
      this.subcategoriesDropdownHelperModels = this.subcategoriesDropdownHelperModels.filter(x => x.categoryId !== category);
      this.listMenuItems = this.listMenuItems.filter(x => x.companyMenuCategoryId !== category);
      this.listMenuItemsSelected = this.listMenuItemsSelected.filter(x => x.companyMenuCategoryId !== category);
    }
  }

  onChangeSubcategory(subcategory: any, initialAdd: boolean = false, selectAll: boolean = true) {
    subcategory = subcategory.itemValue ? subcategory.itemValue : subcategory;
    if (this.menuModel.subcategoryIds.includes(subcategory) || initialAdd) {
      // on select category
      this.loadMenuItemsForSubCategory(subcategory, selectAll);
    } else {
      // on deselect category
      this.listMenuItems = this.listMenuItems.filter(x => x.companyMenuCategoryId !== subcategory);
      this.listMenuItemsSelected = this.listMenuItemsSelected.filter(x => x.companyMenuCategoryId !== subcategory);
    }
  }

  loadMenuItemsForCategory(categoryId: string, selectAll: boolean = true) {
    this.spinner = true;
    this.menuService.listItemsForCategory(categoryId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.spinner = false;
          this.listMenuItems = [...this.listMenuItems, ...response];
          if (selectAll) {
            this.listMenuItemsSelected = [...this.listMenuItemsSelected, ...response];
          }
          this.filterMenuItems();
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  loadMenuItemsForSubCategory(subcategoryId: string, selectAll: boolean = true) {
    this.spinner = true;
    this.menuService.listItemsForSubcategory(subcategoryId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.spinner = false;
          this.listMenuItems = [...this.listMenuItems, ...response];
          if (selectAll) {
            this.listMenuItemsSelected = [...this.listMenuItemsSelected, ...response];
          }
          this.filterMenuItems();
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  private loadMenuDetails() {
    this.menuModel = new AddOrEditMenuModel();
    this.spinner = true;
    this.menuService.viewMenu(this.menuId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.spinner = false;
          this.menuModel = response;
          this.selectedCurrency = this.menuModel.defaultCurrencyId;
          this.menuModel.categoryIds = new Array<string>();
          this.menuModel.subcategoryIds = new Array<string>();
          response.venueMenuCategories.forEach((category: any) => {
            this.menuModel.categoryIds.push(category.companyMenuCategoryId);
            this.onChangeCategory(category.companyMenuCategoryId, false);
            category.childVenueMenuCategories.forEach((subcategory: any) => {
              if (!this.menuModel.subcategoryIds.includes(subcategory.companyMenuCategoryId)) {
                this.menuModel.subcategoryIds.push(subcategory.companyMenuCategoryId);
                this.onChangeSubcategory(subcategory.companyMenuCategoryId, true, false);
              }

              subcategory.venueMenuItems.forEach((menuItem: any) => {
                this.addOrRemoveMenuItemInUpdate(menuItem, subcategory.companyMenuCategoryId);
              });
            });
            category.venueMenuItems.forEach((menuItem: any) => {
              this.addOrRemoveMenuItemInUpdate(menuItem, category.companyMenuCategoryId);
            });
          });
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({ severity: 'error', summary: "System error", life: 2000 });
        }
      });
  }

  addOrRemoveMenuItemInUpdate(menuItem: any, companyMenuCategoryId: string) {
    if (this.listMenuItemsSelected.find(m => m.id === menuItem.id) === undefined) {
      let menuItemObject = new MenuItemModel();
      menuItemObject.description = menuItem.description;
      menuItemObject.id = menuItem.companyMenuItemId;
      menuItemObject.name = menuItem.name;
      menuItemObject.pictureURL = menuItem.pictureURL;
      menuItemObject.price = menuItem.price;
      menuItemObject.priceWithTax = menuItem.priceWithTax;
      menuItemObject.companyMenuCategoryId = companyMenuCategoryId;
      this.listMenuItemsSelected.push(menuItemObject);
    }
  }

  filterMenuItems() {
    this.listMenuItems = this.uniqueArrayById(this.listMenuItems);
    this.listMenuItemsSelected = this.uniqueArrayById(this.listMenuItemsSelected);
  }

  uniqueArrayById(data: any) {
    let resArr: any[] = [];
    data.filter((item: any) => {
      let i = resArr.findIndex(x => (x.id == item.id));
      if(i <= -1){
        resArr.push(item);
      }
      return null;
    });
    return resArr;
  }

  buildRequestBody(model: AddOrEditMenuModel) {
    let requestBody = new AddOrEditMenuRequestModel();
    requestBody.id = model.id;
    model.categories = new Array<CategoryToAddModel>();
    model.categoryIds.forEach(categoryId => {
      let categoryToAdd = new CategoryToAddModel();
      categoryToAdd.id = categoryId;
      categoryToAdd.menuItems = this.listMenuItemsSelected.filter(x => x.companyMenuCategoryId === categoryId).map((x: MenuItemModel) => ({id: x.id, visible: true}));
      categoryToAdd.subCategories = new Array<SubcategoryToAddModel>();

      model.subcategoryIds.forEach(subcategoryId => {
        let subcategory = this.subcategoriesDropdownHelperModels.find(x => x.id === subcategoryId);
        if (subcategory && subcategory.categoryId === categoryId) {
          let subcategoryToAdd = new SubcategoryToAddModel();
          subcategoryToAdd.id = subcategoryId;
          subcategoryToAdd.menuItems = this.listMenuItemsSelected.filter(x => x.companyMenuCategoryId === subcategoryId).map((x: MenuItemModel) => ({id: x.id, visible: true}));
          subcategoryToAdd.visible = true;
          categoryToAdd.subCategories.push(subcategoryToAdd);
        }
      });

      model.categories.push(categoryToAdd);
    });

    requestBody.languageId = this.languageIdFromLocalStorage();
    requestBody.companyId = this.companyId;
    requestBody.name = model.name;
    requestBody.defaultCurrencyId = this.selectedCurrency;
    requestBody.description = model.description;
    requestBody.categories = model.categories;

    return requestBody;
  }

  public addOrRemoveMenuItem(menu: any): void {
    if (this.listMenuItemsSelected.find(m => m.id === menu.id)) {
      this.listMenuItemsSelected = this.listMenuItemsSelected.filter(m => m.id != menu.id);
    } else {
      this.listMenuItemsSelected.push(menu);
    }
  }

  public isMenuSelected(menu: any): boolean {
    return !this.listMenuItemsSelected.find(m => m.id === menu.id);
  }

  languageIdFromLocalStorage(): number {
    const langIdFromStorage = localStorage.getItem('languageId');

    if (langIdFromStorage) {
      return parseInt(langIdFromStorage);
    } else {
      return 1;
    }
  }
  //#endregion
}
